import axios, {AxiosProgressEvent, AxiosRequestConfig, CancelToken} from 'axios';
import mime from 'mime-types';
import parseFileName from '../../utils/fileUtils/parseFileName';
import apiV2Service from '../api/api-v2.service';
import apiService from '../api/api.service';
import {ApiOptions} from '../api/apiTypes';
import {ClinchApplicationError} from '../errorHandling/errorHandling.types';
import {JobExecutionParams} from '../jobs/jobs.types';
import {VideoUploadType} from './fileUpload.types';
import {onUploadMultipleFilesProgress} from './utils/onUploadMultipleFilesProgress';
import waitForJobWithoutBGProcess from '../jobs/utils/waitForJobWithoutBGP';
import {JobResult} from '../jobs/jobs.types';

export const basePath = 'FileUpload';

export interface PreSignedUrlResponse {
  fileKey: string;
  finalCdnUrl: string;
  finalS3Url: string;
  maxSizeAllowedMb: number;
  presignedUrl: string;
}

// interface JobExecutionImageParams {
//   executionId: number;
//   type: number;
// }

export async function uploadFileToS3({
  url,
  file,
  contentType,
  onUploadProgress,
  cancelToken,
}: {
  url: string;
  file: File;
  contentType?: string;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  cancelToken?: CancelToken;
}): Promise<void> {
  const config: AxiosRequestConfig = {
    onUploadProgress,
    cancelToken,
    headers: {
      'Content-Type': contentType ?? mime.contentType(file.name),
      Accept: '*/*',
    },
  };

  try {
    await axios.put(url, file, config);
  } catch (e) {
    throw new ClinchApplicationError('Failed Upload File');
  }
}

async function getPreSignedUrlForPlacementsUpload({
  campaignId,
  fileExtension,
  isDcm = false,
}: {
  campaignId: number;
  fileExtension: string;
  isDcm?: boolean;
}): Promise<PreSignedUrlResponse> {
  const {
    data: [urlsObj],
  } = await apiService.executeGetRequest<PreSignedUrlResponse[]>(
    `${basePath}/GetPresignedUrlForPlacementFeedFileUpload`,
    {campaignId, fileType: fileExtension, isDCM: isDcm},
  );

  return urlsObj;
}

async function getPresignedUrlForAssetsLibrary({
  feedId,
  fileNameWithExtension,
  fileType,
  uploadType,
}: {
  feedId: number;
  fileNameWithExtension: string;
  fileType: string;
  uploadType: string;
}): Promise<PreSignedUrlResponse> {
  const {
    data: [urlsObj],
  } = await apiService.executeGetRequest<PreSignedUrlResponse[]>(
    `${basePath}/GetPresignedUrlForAssetsLibraryFilesWhichNotVideoOrImage`,
    {objectId: feedId, uploadType, fileNameWithExtension, fileType},
  );

  return urlsObj;
}

async function getPreSignedUrlForLocationFeedUpload({
  locationFeedId,
  contentType,
  fileExtension,
}: {
  locationFeedId: number;
  contentType: string;
  fileExtension: string;
}): Promise<PreSignedUrlResponse> {
  const {data: urlObj} = await apiV2Service.executeGetRequest<PreSignedUrlResponse>(
    `${basePath}/GetPresignedUrlForLocationFeedFileUpload`,
    {contentType, fileExtension, locationFeedId},
  );

  return urlObj;
}

async function getPresignedUrlForFeedFileUpload({
  feedId,
  contentType,
  fileExtension,
}: {
  feedId: number;
  contentType: string;
  fileExtension: string;
}): Promise<PreSignedUrlResponse> {
  const {data: urlObj} = await apiV2Service.executeGetRequest<PreSignedUrlResponse>(
    `${basePath}/GetPresignedUrlForProductFeedFileUpload`,
    {contentType, fileExtension, feedId},
  );

  return urlObj;
}

async function getPresignedUrlForCampaignImageFileUpload(payload: {
  campaignId: number;
  nodeId: number;
  contentType: string;
  fileExtension: string;
}) {
  return apiV2Service
    .executeGetRequest<PreSignedUrlResponse>(
      `${basePath}/GetPresignedUrlForCampaignImageFileUpload`,
      payload,
    )
    .then((res) => res.data);
}

async function getPresignedUrlForDOOHScreensUpload() {
  return apiV2Service
    .executeGetRequest<{
      presignedUrl: string;
      filePath: string;
    }>(`${basePath}/GetPresignedUrlForDoohScreens`)
    .then((res) => res.data);
}

async function getPresignedUrlForTrackersImport(fileName: string) {
  return apiV2Service
    .executeGetRequest<PreSignedUrlResponse>(`${basePath}/GetPresignedUrlForTrackersUpload`, {
      fileName,
    })
    .then((res) => res.data);
}

async function getPresignedUrlForUpload(fileType: string, fileName?: string) {
  return apiV2Service
    .executeGetRequest<PreSignedUrlResponse>('FileUpload/GetPresignedUrlForUpload', {
      mimeType: fileType,
      ...(fileName ? {fileName} : {}),
    })
    .then((res) => res.data);
}

async function getPresignedUrlForCopilot(fileType: string, fileName?: string) {
  const {data} = await apiV2Service.executeGetRequest<PreSignedUrlResponse>(
    'FileUpload/GetPresignedUrlForCopilot',
    {
      mimeType: fileType,
      ...(fileName ? {fileName} : {}),
    },
  );

  return data;
}

// async function getPresignedUrlForTemplateImageFileUpload(file: File, templateId: number) {
//   return apiV2Service.executePostRequest<PreSignedUrlResponse>(
//     'FileUpload/GetPresignedUrlForTemplateImageFileUpload',
//     {},
//     {
//       fileKey: file.name,
//       templateId: templateId,
//       mimeType: file.type,
//     },
//   );
// }

// async function getPresignedUrlForNonTemplateImageFileUpload(file: File) {
//   return apiV2Service.executePostRequest<PreSignedUrlResponse>(
//     'FileUpload/GetPresignedUrlForNonTemplateImageFileUpload',
//     {},
//     {
//       fileKey: file.name,
//       mimeType: file.type,
//     },
//   );
// }

async function getPresignedUrlForContentFeedFileUpload({
  feedId,
  contentType,
  fileExtension,
}: {
  feedId: number;
  contentType: string;
  fileExtension: string;
}): Promise<PreSignedUrlResponse> {
  const {data: urlObj} = await apiV2Service.executeGetRequest<PreSignedUrlResponse>(
    `${basePath}/GetPresignedUrlForContentFeedFileUpload`,
    {contentType, fileExtension, feedId},
  );

  return urlObj;
}

// async function processImageFileUpload({
//   imageType,
//   fileKey,
//   file,
//   keyType,
//   objectId,
//   options,
// }: {
//   imageType: string;
//   fileKey: string;
//   file: File;
//   keyType: 'creativeId' | 'templateId';
//   objectId: number;
//   options: ApiOptions;
// }) {
//   return await apiV2Service.executePostRequest<JobExecutionImageParams>(
//     `FileUpload/ProcessImageFileUpload${imageType}`,
//     {},
//     {
//       filePath: file.name,
//       fileKey,
//       imageUploadType: imageType,
//       mimeType: file.type,
//       [keyType]: objectId,
//     },
//     options,
//   );
// }

async function uploadImage({
  file,
  options = {},
}: {
  file: File;
  objectId: number;
  imageType: string;
  keyType: 'creativeId' | 'templateId';
  options?: ApiOptions;
}): Promise<string> {
  const {presignedUrl, fileKey} = await getPresignedUrlForUpload(file.type, file.name);

  await uploadFileToS3({
    url: presignedUrl,
    file: file,
    contentType: file.type,
    cancelToken: options?.cancelToken,
  });

  return fileKey;
}

async function uploadVideo({
  objectId,
  uploadType,
  file,
  options,
  paramKey,
  width,
  height,
  extension = 'mp4',
  contentType = 'video/mp4',
}: {
  file: File;
  objectId: number;
  uploadType: VideoUploadType;
  width: number;
  height: number;
  options?: ApiOptions;
  paramKey?: string;
  extension?: string;
  contentType?: string;
}): Promise<string> {
  const {
    data: [urlsObj],
  } = await apiService.executeGetRequest<[PreSignedUrlResponse]>(`${basePath}/GetPresignedUrl`, {
    objectId,
    uploadType,
    fileExt: extension,
    contentType: contentType,
    ...(paramKey ? {paramKey} : {}),
  });

  const {fileKey, presignedUrl} = urlsObj;
  await uploadFileToS3({
    url: presignedUrl,
    file,
    contentType: contentType,
    cancelToken: options?.cancelToken,
    onUploadProgress: options?.onUploadProgress
      ? onUploadMultipleFilesProgress(0, 0.8, options.onUploadProgress)
      : undefined,
  });

  const {
    data: {executionId},
  } = await apiV2Service.executePostRequest<JobExecutionParams>(
    `${basePath}/TranscodeVideoJob`,
    {uploadType, objectId, width, height, inputFileKey: fileKey},
    {},
  );

  const {
    result: {finalCdnUrl},
  } = await waitForJobWithoutBGProcess<PreSignedUrlResponse & JobResult>({
    jobExecutionId: executionId,
  });

  return encodeURI(finalCdnUrl);
}

async function getPresignedUrlForAEFile({
  fileName,
  templateId,
  width,
  height,
}: {
  fileName: string;
  templateId: number;
  width: number;
  height: number;
}): Promise<PreSignedUrlResponse> {
  const url = `${basePath}/GetPresignedUrlForAeAssets`;
  const {fileExtension} = parseFileName(fileName);
  const contentType = mime.contentType(fileExtension || '');

  const {
    data: [urlsObj],
  } = await apiService.executeGetRequest<PreSignedUrlResponse[]>(url, {
    fileFullName: fileName,
    width,
    height,
    templateId,
    contentType,
  });

  return urlsObj;
}

async function getPresignedUrlForCampaignExcelUpload({
  fileName,
}: {
  fileName: string;
}): Promise<PreSignedUrlResponse> {
  const {data: urlObj} = await apiV2Service.executeGetRequest<PreSignedUrlResponse>(
    `${basePath}/GetPresignedUrlForCampaignExcelUpload`,
    {fileName},
  );

  return urlObj;
}

export const fileUploadApi = {
  getPreSignedUrlForPlacementsUpload,
  getPreSignedUrlForLocationFeedUpload,
  uploadFileToS3,
  getPresignedUrlForAEFile,
  uploadVideo,
  getPresignedUrlForAssetsLibrary,
  getPresignedUrlForFeedFileUpload,
  getPresignedUrlForCampaignImageFileUpload,
  getPresignedUrlForDOOHScreensUpload,
  getPresignedUrlForTrackersImport,
  uploadImage,
  getPresignedUrlForUpload,
  getPresignedUrlForContentFeedFileUpload,
  getPresignedUrlForCampaignExcelUpload,
  getPresignedUrlForCopilot,
};
